import React, {useContext, useState} from "react";
import {Box, Button, Checkbox, CircularProgress, FormHelperText, InputAdornment, Stack, TextField, Typography} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {ArrowLeft01Icon, GoogleIcon} from "@hugeicons/core-free-icons";
import {toast} from "react-toastify";
import {SignInContext} from "../../../context/SignInContext";
import {AuthLayout} from "../components/AuthLayout";
import {enterToNext} from "../components/enterToNext";
import {AuthPasswordField} from "../components/AuthPasswordField";
import {isPasswordValid} from "../components/PasswordChecklist";
import {PasswordStrengthBar} from "../components/PasswordStrengthBar";
import {a, fieldSx, linkSx, primaryButtonSx, PRIVACY_URL, secondaryButtonSx, TERMS_URL} from "../components/authStyles";
import {mapToUrl} from "../../../utils/functions";
import {CountryCodeSelect, DEFAULT_COUNTRY, dialCodeOf} from "../components/CountryCodeSelect";

const STEPS = 2;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// What each step asks for
const STEP_DESCRIPTIONS = [
  "Start with your email and a password.",
  "Tell us a little about you. Phone and company are optional.",
];

export const RegisterPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    lastname: "",
    email: "",
    password: "",
    confirmPassword: "",
    country: DEFAULT_COUNTRY,
    phone_number: "",
    company_name: "",
    acceptedTerms: false,
  })
  const {onRegister} = useContext(SignInContext);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [step, setStep] = useState(0);

  const onChange = (e) => {
    const key = e.target.name;
    const value = e.target.value;

    updateForm(key, value);
    setErrors(prev => ({...prev, [key]: ""}));
  }

  const updateForm = (key, value) => {
    setFormData(prev => ({...prev, [key]: value}));
  }

  // Step 2 — who the user is
  const validateAbout = () => {
    const nextErrors = {};

    if (!formData?.name?.trim()) nextErrors.name = "Name is required";
    if (!formData?.lastname?.trim()) nextErrors.lastname = "Last name is required";
    // optional, but must be digits when given (spaces between them are fine)
    const phoneDigits = (formData?.phone_number || "").replace(/\s/g, "");
    if (phoneDigits && !/^\d+$/.test(phoneDigits)) {
      nextErrors.phone_number = "Phone number can contain numbers only";
    }
    return nextErrors;
  }

  // Step 1 — email and password
  const validateSecurity = () => {
    const nextErrors = {};

    if (!formData?.email?.trim()) {
      nextErrors.email = "Email is required";
    } else if (!EMAIL_PATTERN.test(formData.email.trim())) {
      nextErrors.email = "Please enter a valid email address";
    }
    if (!formData?.password) {
      nextErrors.password = "Password is required";
    } else if (!isPasswordValid(formData.password)) {
      nextErrors.password = "Password doesn't meet all the requirements";
    }
    if (!formData?.confirmPassword) {
      nextErrors.confirmPassword = "Please confirm your password";
    } else if (formData.password !== formData.confirmPassword) {
      nextErrors.confirmPassword = "Passwords do not match";
    }
    if (!formData.acceptedTerms) nextErrors.acceptedTerms = "You must accept the Terms & Conditions and Privacy Policy";
    return nextErrors;
  }

  const registerHandle = async (e) => {
    e.preventDefault();

    if (step === 0) {
      const nextErrors = validateSecurity();
      setErrors(nextErrors);
      if (Object.keys(nextErrors).length === 0) setStep(1);
      return;
    }

    const nextErrors = validateAbout();
    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      return;
    }
    try {
      setLoading(true);
      const {confirmPassword, country, acceptedTerms, ...payload} = formData;
      // the number goes out with its country code, digits only (e.g. 38970123456); no number, no code
      const local = formData.phone_number.replace(/\s/g, "").replace(/^0+/, "");
      await onRegister({
        ...payload,
        phone_number: local ? `${dialCodeOf(country)}${local}` : "",
        username: formData?.email,
        termsAccepted: acceptedTerms,
      });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }

  const goBack = () => {
    setErrors({});
    setStep(0);
  }

  const submitDisabled = loading || (step === 0
    ? !formData?.email || !formData?.password || !formData?.confirmPassword || !formData?.acceptedTerms
    : !formData?.name || !formData?.lastname);

  return (
    <AuthLayout
      maxWidth={440}
      kicker={`Sign up · Step ${step + 1} of ${STEPS}`}
      title={"Create your account"}
      description={STEP_DESCRIPTIONS[step]}
      prompt={"Already have an account?"}
      linkText={"Sign in"}
      linkTo={"/login"}
    >
      {/*REGISTER FORM*/}
      <Stack component={"form"} onSubmit={registerHandle} onKeyDown={enterToNext} direction={"column"} gap={0.5} sx={{width: '100%'}}>
        {/*STEP INDICATOR*/}
        <Stack direction={"row"} gap={0.75} sx={{mb: 2}}>
          {Array.from({length: STEPS}, (_, i) => (
            <Box key={i} sx={{
              flex: 1, height: 4, borderRadius: "980px",
              bgcolor: i <= step ? a.accent : "rgba(132, 145, 247, 0.2)",
              transition: "background-color .3s ease",
            }}/>
          ))}
        </Stack>

        {step === 0 ? (
          <>
            <TextField
              variant={"filled"}
              required
              autoFocus
              fullWidth
              label={"Email"}
              type={"email"}
              autoComplete={"email"}
              value={formData?.email}
              name={"email"}
              onChange={onChange}
              placeholder={"you@company.com"}
              error={!!errors.email}
              helperText={errors.email || " "}
              sx={fieldSx}
            />
            <AuthPasswordField
              required
              name={"password"}
              label={"Password"}
              autoComplete={"new-password"}
              value={formData?.password}
              placeholder={"Create a password"}
              onChange={onChange}
              error={!!errors.password}
              helperText={errors.password || " "}
            />
            {(formData?.password || errors.password) && (
              <PasswordStrengthBar password={formData?.password || ""} showErrors={!!errors.password}/>
            )}
            <AuthPasswordField
              required
              name={"confirmPassword"}
              label={"Confirm password"}
              autoComplete={"new-password"}
              value={formData?.confirmPassword}
              placeholder={"Re-enter your password"}
              onChange={onChange}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword || " "}
            />
            <Stack direction={"row"} alignItems={"flex-start"} gap={0.5}>
              <Checkbox
                name={"acceptedTerms"}
                checked={formData.acceptedTerms}
                onChange={(e) => {
                  updateForm("acceptedTerms", e.target.checked);
                  setErrors(prev => ({...prev, acceptedTerms: ""}));
                }}
                sx={{p: 0.5, color: errors.acceptedTerms ? a.error : a.muted, "&.Mui-checked": {color: a.accent}}}
              />
              <Typography sx={{fontSize: 13, color: a.text, pt: 0.5}}>
                I agree to the{" "}
                <Box component={"a"} href={TERMS_URL} target={"_blank"} rel={"noreferrer"} sx={linkSx}>Terms & Conditions</Box>
                {" "}and{" "}
                <Box component={"a"} href={PRIVACY_URL} target={"_blank"} rel={"noreferrer"} sx={linkSx}>Privacy Policy</Box>
              </Typography>
            </Stack>
            <FormHelperText error sx={{ml: 0.5, minHeight: 20}}>{errors.acceptedTerms || " "}</FormHelperText>
          </>
        ) : (
          <>
            <Stack direction={{xs: "column", sm: "row"}} gap={1.5}>
              <TextField
                variant={"filled"}
                required
                autoFocus
                fullWidth
                label={"First name"}
                autoComplete={"given-name"}
                value={formData?.name}
                name={"name"}
                onChange={onChange}
                placeholder={"Jane"}
                error={!!errors.name}
                helperText={errors.name || " "}
                sx={fieldSx}
              />
              <TextField
                variant={"filled"}
                required
                fullWidth
                label={"Last name"}
                autoComplete={"family-name"}
                value={formData?.lastname}
                name={"lastname"}
                onChange={onChange}
                placeholder={"Doe"}
                error={!!errors.lastname}
                helperText={errors.lastname || " "}
                sx={fieldSx}
              />
            </Stack>
            <TextField
              variant={"filled"}
              fullWidth
              label={"Phone number"}
              type={"tel"}
              autoComplete={"tel-national"}
              inputProps={{inputMode: "numeric"}}
              value={formData?.phone_number}
              name={"phone_number"}
              onChange={onChange}
              error={!!errors.phone_number}
              helperText={errors.phone_number || " "}
              sx={fieldSx}
              // the label stays raised, so the country prefix and the number share one line under it
              InputLabelProps={{shrink: true}}
              InputProps={{
                startAdornment: (
                  <InputAdornment position={"start"}>
                    <CountryCodeSelect value={formData.country} onChange={(country) => updateForm("country", country)}/>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              variant={"filled"}
              fullWidth
              label={"Company"}
              autoComplete={"organization"}
              value={formData?.company_name}
              name={"company_name"}
              onChange={onChange}
              placeholder={"Where do you work?"}
              helperText={" "}
              sx={fieldSx}
            />
          </>
        )}

        <Button
          fullWidth
          type={"submit"}
          disabled={submitDisabled}
          sx={{...primaryButtonSx, mt: 0.5}}
          startIcon={loading ? <CircularProgress size={16} color={"inherit"}/> : null}
        >
          {step === 0 ? "Continue" : "Create account"}
        </Button>
        {step === 0 && (
          <>
            <Typography sx={{textAlign: "center", color: a.muted, fontSize: 12.5, py: 1.5}}>or</Typography>
            <Button
              component={"a"}
              href={formData.acceptedTerms
                ? mapToUrl(`/api/auth/oauth2/google?termsAccepted=true&redirect_uri=${encodeURIComponent(`${window.location.origin}/oauth2/redirect`)}`, process.env.REACT_APP_USERMANAGEMENT_GATEWAY)
                : undefined}
              disabled={!formData.acceptedTerms}
              fullWidth
              startIcon={<HugeiconsIcon icon={GoogleIcon} size={18}/>}
              sx={secondaryButtonSx}
            >
              Sign up with Google
            </Button>
          </>
        )}
        {step === 1 && (
          <Button
            onClick={goBack}
            disabled={loading}
            disableRipple
            startIcon={<HugeiconsIcon icon={ArrowLeft01Icon} size={16}/>}
            sx={{
              alignSelf: "center", mt: 0.5, px: 1, textTransform: "none", fontSize: 14, fontWeight: 500,
              color: a.text, bgcolor: "transparent", "&:hover": {bgcolor: "transparent", color: a.ink},
            }}
          >
            Back
          </Button>
        )}
      </Stack>
    </AuthLayout>
  )
}
